import { Fragment, useState } from 'react';
import Legend from './Legend/Legend';
import './Results.css';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Button } from '@mui/material';
import { useStoreState } from 'pullstate';
import { CurrentScan } from '../../stores/CurrentScan';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import HighlightOnButton from './HighlightOnButton/HighlightOnButton';
import PlagiarismAccordions from './PlagPanel/PlagiarismAccordions/PlagiarismAccordions';
import ResultsBar from './ResultsBar/ResultsBar';

const Results = ({ data, plagScan }) => {
    const location = window.location.pathname.split('/')[1];
    const { mode, highlightOn } = useStoreState(CurrentScan);
    const highlightClass = highlightOn ? 'highlight' : '';
    const [value, setValue] = useState('ai');

    // Access the scan container
    const fullScan = data?.scanData?.fullScanData ?? data?.fullScanData;

    // Normalize AI results
    const aiResults =
        fullScan?.scan_results?.aiResults ??
        (fullScan?.results?.ai
            ? {
                  blocks: fullScan.results.ai.blocks || [],
                  fake: fullScan.results.ai.confidence?.AI || 0,
                  real: fullScan.results.ai.confidence?.Original || 0,
                  classification: fullScan.results.ai.classification,
              }
            : null);

    // Normalize Plagiarism results
    const plagResults =
        fullScan?.scan_results?.plagResults ??
        (fullScan?.results?.plagiarism
            ? {
                  documentScore: fullScan.results.plagiarism.score || 0,
                  results: fullScan.results.plagiarism.results || [],
              }
            : null);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        CurrentScan.update((s) => {
            s.mode = newValue;
        });
    };

    const decodeHtmlEntities = (text) => {
        const parser = new DOMParser();
        const decodedString = parser.parseFromString(text, 'text/html').body.textContent;
        return decodedString ?? text;
    };

    return (
        <Grid container className='Results-container'>
            <TabContext value={value}>
                <Grid container xs={12}>
                    <Grid item xs={12} md={8} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList
                            onChange={handleChange}
                            variant='scrollable'
                            scrollButtons='auto'
                            aria-label='lab API tabs example'
                        >
                            <Tab label='AI Results' value='ai' />
                            <Tab
                                label={`Plagiarism Results (${plagResults?.documentScore ?? 0})`}
                                value='plagiarism'
                            />
                        </TabList>
                    </Grid>
                    {aiResults && mode === 'ai' && (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                            <HighlightOnButton />
                        </Grid>
                    )}
                </Grid>

                {/* AI Results */}
                <TabPanel value='ai' sx={{ padding: '24px 0', width: '100%' }}>
                    <Grid item xs={12}>
                        {mode !== 'plagiarism' && (
                            <Box className='Results' id='resultsContainer'>
                                {aiResults?.blocks?.map((block, i) => {
                                    const decodedText = decodeHtmlEntities(block.text);
                                    return (
                                        <div
                                            key={i}
                                            className={`${highlightClass} block ${
                                                block.result.fake > 0.75
                                                    ? 'red'
                                                    : block.result.fake > 0.4
                                                    ? 'pink'
                                                    : 'green'
                                            }`}
                                            style={{ '--strength': block.result.fake }}
                                        >
                                            {decodedText.split('\n').map((line, j, arr) => (
                                                <Fragment key={j}>
                                                    {i !== 0 && j === 0 && (
                                                        <div className='spacer' />
                                                    )}
                                                    <span className='line'>{line}</span>
                                                    {j !== arr.length - 1 && <br />}
                                                </Fragment>
                                            ))}
                                        </div>
                                    );
                                })}
                            </Box>
                        )}
                    </Grid>
                    {highlightOn && mode !== 'plagiarism' && (
                        <Grid item xs={12} sx={{ minHeight: '70px', overflow: 'visible' }}>
                            <Legend />
                        </Grid>
                    )}
                    {aiResults && mode === 'ai' && (
                        <Grid item xs={12}>
                            <div style={{ height: '1rem' }} />
                            <ResultsBar
                                val={(aiResults.fake * 100).toFixed(2)}
                                label='Overall AI Confidence'
                                shownVal={(aiResults.fake * 100).toFixed(2) + '%'}
                            />
                        </Grid>
                    )}
                </TabPanel>

                {/* Plagiarism Results */}
                <TabPanel value='plagiarism' sx={{ padding: '24px 0', width: '100%' }}>
                    {plagScan && mode === 'plagiarism' && (
                        <Grid item xs={12}>
                            <div style={{ height: '1rem' }} />
                            <ResultsBar
                                val={(plagResults?.documentScore || 0).toFixed(2)}
                                label='Plagiarism Score'
                                shownVal={`${(plagResults?.documentScore || 0).toFixed(2)}%`}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {location === 'ext-scans' ? (
                            <Button
                                href={`https://app.passed.ai/scans/${data.id}`}
                                variant='contained'
                                color='secondary'
                                target='_blank'
                            >
                                View Full Results
                            </Button>
                        ) : (
                            <PlagiarismAccordions results={plagResults} />
                        )}
                    </Grid>
                </TabPanel>
            </TabContext>
        </Grid>
    );
};

export default Results;
